import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, Bell, LogOut, LayoutDashboard, Vault } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthContext } from '@/contexts/auth';
import { useDealerStatus } from '@/hooks/useDealerStatus';
import { debugError } from '@/utils/debugLogger';

interface UserAvatarDropdownProps {
  onNavigate?: () => void;
}

export function UserAvatarDropdown({ onNavigate }: UserAvatarDropdownProps) {
  const { user, signOut } = useAuthContext();
  const { hasActivatedDealers } = useDealerStatus();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      if (onNavigate && typeof onNavigate === 'function') {
        onNavigate();
      }
    } catch (error) {
      debugError('auth', "Logout error", error, 'UserAvatarDropdown');
    }
  };

  const handleMenuItemClick = () => {
    if (onNavigate && typeof onNavigate === 'function') {
      onNavigate();
    }
  };

  // Get display name and avatar
  const displayName = user?.name || user?.profile?.fullName || user?.email?.split('@')[0] || 'Utente';
  const avatarUrl = user?.profile?.avatarUrl;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center space-x-2 p-1 rounded-full hover:bg-accent transition-colors">
          <Avatar className="h-10 w-10">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              <User className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-56 z-[9999]" align="end">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{displayName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem asChild>
          <Link to="/profilo" onClick={handleMenuItemClick} className="flex items-center">
            <User className="mr-2 h-4 w-4" />
            <span>Il mio Profilo</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/vault" onClick={handleMenuItemClick} className="flex items-center">
            <Vault className="mr-2 h-4 w-4" />
            <span>Il mio Vault</span>
          </Link>
        </DropdownMenuItem>
        
        {hasActivatedDealers && (
          <DropdownMenuItem asChild>
            <Link to="/dashboard-dealer" onClick={handleMenuItemClick} className="flex items-center">
              <LayoutDashboard className="mr-2 h-4 w-4" />
              <span>Dashboard Dealer</span>
            </Link>
          </DropdownMenuItem>
        )}
        
        <DropdownMenuItem asChild>
          <Link to="/profilo?tab=impostazioni" onClick={handleMenuItemClick} className="flex items-center">
            <Settings className="mr-2 h-4 w-4" />
            <span>Impostazioni</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild>
          <Link to="/avvisi" onClick={handleMenuItemClick} className="flex items-center">
            <Bell className="mr-2 h-4 w-4" />
            <span>Avvisi Prezzo</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={handleLogout} 
          className="flex items-center text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Esci</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}